import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import filter from 'lodash/filter';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  FlagIcon,
  CalendarIcon,
  BeakerIcon,
  ClipboardListIcon,
} from '@heroicons/react/outline';
import PatientRecord from '../../../../common/models/PatientRecord';
import { default as ProjectMilestoneSelect } from '../../../../common/selectors/ProjectMilestone';
import {
  ACTIVITY_STATE__COMPLETED,
  ACTIVITY_STATE__ACTIVE,
  ACTIVITY_STATE__EXPIRED,
  ACTIVITY_STATE__SCHEDULED,
  ACTIVITY_STATE__PLANNED,
  ACTIVITY_STATE__SUSPENDED,
  PARTICIPANT_STATE__CANCELED,
} from '../../../../common/constants';
import NotificationLog from '../../../../components/NotificationLog';
import TimelineEvent from './TimelineEvent';
import { getMilestoneStatus } from './helpers';
import {
  getRecordStatusLabel,
  getRecordStatusType,
  RECORD_TYPE_APPOINTMENT,
  RECORD_TYPE_DRUG,
  RECORD_TYPE_PROCEDURE,
} from '../../../EpisodesOfCare/helpers';

const ActivityButton = ({
  patientRecord,
  state,
  activityId,
  milestoneId,
  recipientId,
  onMilestoneClick,
  isDischarged,
  children,
  reviewHistory,
  notification,
  eventId,
  clinicalEvents,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const milestone = useSelector(
    ProjectMilestoneSelect.one().whereIdEquals(milestoneId),
  );

  const { projectId, questionnaires } = patientRecord;

  // Get clinical event data if this is a clinical event activity
  const clinicalEventData =
    eventId && clinicalEvents ? clinicalEvents[eventId] : null;
  const { domain, start, end, status, context = {} } = clinicalEventData || {};

  const getMilestoneDate = () => {
    switch (state) {
      case ACTIVITY_STATE__COMPLETED:
        return patientRecord.getActivityCompletedAt(activityId);
      case ACTIVITY_STATE__ACTIVE:
      case ACTIVITY_STATE__SUSPENDED:
      case ACTIVITY_STATE__EXPIRED:
        return patientRecord.getActivityDateEnd(activityId);
      case ACTIVITY_STATE__SCHEDULED:
      case ACTIVITY_STATE__PLANNED:
        return patientRecord.getActivityDateStart(activityId);
      default:
        return null;
    }
  };

  const nQuestionnaires = filter(questionnaires, (q) => {
    return (
      q.state !== PARTICIPANT_STATE__CANCELED && q.activityId === activityId
    );
  }).length;
  const isNonActionable = milestone && milestone.isNonActionable();
  const isClinicalEvent = milestone && milestone.isClinicalEventMilestone();
  const isMilestoneAttachToClinicalEvent =
    milestone && milestone.isMilestoneAttachToClinicalEvent();

  const isDisabled = isDischarged && state !== ACTIVITY_STATE__COMPLETED;

  const handleMilestoneClick = () => {
    // Determine the activeKey based on activity type
    let activeKey = 'questionnaires';

    if (clinicalEventData) {
      // For clinical events, open the clinical event tab
      activeKey = 'clinicalEvent';
    } else if (isNonActionable) {
      // For non-actionable milestones, open the activity/reminders tab
      activeKey = 'activity';
    }

    return onMilestoneClick({
      milestoneId,
      recipientId,
      activityId,
      activeKey,
    });
  };
  const handleStatusClick = () =>
    onMilestoneClick({
      milestoneId,
      recipientId,
      activityId,
      activeKey: 'activity',
    });

  const reviewResponses = milestone ? milestone.reviewResponses : false;
  const reviewResponsesTypes = milestone
    ? milestone.reviewResponsesTypes
    : null;
  const isEmpty = !isNonActionable && nQuestionnaires === 0;
  const props = {
    onMilestoneClick: handleMilestoneClick,
    onStatusClick: handleStatusClick,
    projectId,
    activityId,
    patientRecord,
    isNonActionable,
    date: getMilestoneDate(),
    name: milestone ? milestone.getName() : 'Custom',
    isEmpty: !isNonActionable && nQuestionnaires === 0,
    className: 'zedoc-tour__milestone',
    'data-testid': `project-milestone-${
      milestone ? milestone.getName() : 'Custom'
    }-${patientRecord.recipientName}`,
    reviewHistory,
    reviewResponses: milestone ? milestone.reviewResponses : false,
    reviewResponsesTypes: milestone ? milestone.reviewResponsesTypes : null,
  };

  const reviewIcon = useMemo(() => {
    if (
      reviewResponses &&
      state === ACTIVITY_STATE__COMPLETED &&
      reviewHistory &&
      reviewHistory.length > 0
    ) {
      return <CheckCircleIcon className="w-4 h-4 text-success" />;
    }

    if (
      reviewResponses &&
      state === ACTIVITY_STATE__COMPLETED &&
      (!reviewHistory || reviewHistory.length === 0)
    ) {
      const isOptional = reviewResponsesTypes === 'Not Reviewed - Optional';
      return (
        <ExclamationCircleIcon
          className={`w-4 h-4 ${isOptional ? 'text-info' : 'text-danger-500'}`}
        />
      );
    }

    return null;
  }, [reviewHistory, reviewResponses, reviewResponsesTypes, state]);

  const type = useMemo(() => {
    // For clinical events, use status-based type
    if (clinicalEventData) {
      return getRecordStatusType(status);
    }

    // For regular activities, use state-based type
    switch (state) {
      case ACTIVITY_STATE__COMPLETED:
        return 'success';
      case ACTIVITY_STATE__EXPIRED:
        return 'danger';
      case ACTIVITY_STATE__ACTIVE:
        return 'warning';
      case ACTIVITY_STATE__SCHEDULED:
      case ACTIVITY_STATE__PLANNED:
        return 'info';
      default:
        return null;
    }
  }, [state, clinicalEventData, status]);

  // Clinical event specific icon
  const clinicalEventIcon = useMemo(() => {
    if (!clinicalEventData) return null;

    switch (domain) {
      case RECORD_TYPE_APPOINTMENT:
        return <CalendarIcon className="h-4 w-4 flex-shrink-0" />;
      case RECORD_TYPE_DRUG:
        return <BeakerIcon className="h-4 w-4 flex-shrink-0" />;
      case RECORD_TYPE_PROCEDURE:
        return <ClipboardListIcon className="h-4 w-4 flex-shrink-0" />;
      default:
        return null;
    }
  }, [domain, clinicalEventData]);

  // Clinical event specific title
  const clinicalEventTitle = useMemo(() => {
    if (isMilestoneAttachToClinicalEvent) {
      return t('clinicalEventsMilestones', { count: 1 });
    }

    if (!clinicalEventData) return null;

    switch (domain) {
      case RECORD_TYPE_APPOINTMENT:
        return t('appointments', { count: 1 });
      case RECORD_TYPE_DRUG:
        return t('medications', { count: 1 });
      case RECORD_TYPE_PROCEDURE:
        return t('procedures', { count: 1 });
      default:
        return null;
    }
  }, [domain, t, clinicalEventData, isMilestoneAttachToClinicalEvent]);

  // Clinical event specific name
  const clinicalEventName = useMemo(() => {
    if (!clinicalEventData) return null;

    switch (domain) {
      case RECORD_TYPE_APPOINTMENT:
        return start
          ? `${toIntlDateTimeFormat(language, start, {
              timeStyle: 'short',
            })} • ${(end - start) / 60000} min`
          : null;
      case RECORD_TYPE_DRUG:
        return context?.name;
      case RECORD_TYPE_PROCEDURE: {
        const procedureType = context?.type || 'Procedure';
        // Show linked appointment info if available
        if (context?.appointment?.appointmentStart) {
          const appointmentTime = toIntlDateTimeFormat(
            language,
            context.appointment.appointmentStart,
            { timeStyle: 'short' },
          );
          return `${procedureType} • Appt: ${appointmentTime}`;
        }
        return procedureType;
      }
      default:
        return null;
    }
  }, [domain, context, start, end, language, clinicalEventData]);

  // Get status text for clinical events
  const clinicalEventStatus = useMemo(() => {
    if (!clinicalEventData) return null;

    // Return the status from the clinical event
    return status;
  }, [clinicalEventData, status]);

  return (
    <div
      className={!isDisabled ? 'relative pointer-events-auto' : ''}
      style={
        !isDisabled
          ? {
              zIndex: 10,
            }
          : {}
      }
    >
      <TimelineEvent
        data-testid={`project-milestone-${
          milestone ? milestone.getName() : 'Custom'
        }-${patientRecord.recipientName}`}
        className="zedoc-tour__milestone"
        type={type}
        icon={clinicalEventData ? clinicalEventIcon : <FlagIcon />}
        title={clinicalEventTitle || t('milestones', { count: 1 })}
        state={state}
        status={
          clinicalEventData
            ? getRecordStatusLabel({ status: clinicalEventStatus, t })
            : getMilestoneStatus(t, state, isEmpty)
        }
        projectId={projectId}
        isDisabled={isDisabled}
        reviewIcon={reviewIcon}
        notification={notification}
        onClick={handleMilestoneClick}
        onStatusClick={handleStatusClick}
        isNonActionable={isNonActionable || clinicalEventData}
        isClinicalEvent={isClinicalEvent}
        date={clinicalEventData ? start : getMilestoneDate()}
        name={
          clinicalEventData
            ? clinicalEventName
            : milestone
            ? milestone.getName()
            : 'Custom'
        }
        isEmpty={isEmpty}
      >
        {children}
      </TimelineEvent>
    </div>
  );
};

ActivityButton.propTypes = {
  patientRecord: PropTypes.instanceOf(PatientRecord).isRequired,
  notification: PropTypes.instanceOf(NotificationLog),
  activityId: PropTypes.string.isRequired,
  recipientId: PropTypes.string.isRequired,
  state: PropTypes.string,
  milestoneId: PropTypes.string,
  children: PropTypes.node,
  onMilestoneClick: PropTypes.func,
  isDischarged: PropTypes.bool,
  reviewHistory: PropTypes.arrayOf(PropTypes.object),
  eventId: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  clinicalEvents: PropTypes.object,
};

const noop = () => {};
ActivityButton.defaultProps = {
  notification: null,
  state: null,
  milestoneId: null,
  children: null,
  onMilestoneClick: () => {},
  isDischarged: false,
  reviewHistory: null,
  eventId: null,
  clinicalEvents: {},
};

export default ActivityButton;
